/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useState } from 'react';
import { SubscriptionService } from '@/services/requests';

/** `error` is its own state: the screen shows no trial then, but no sentence either. */
export type TrialStatus = 'loading' | 'eligible' | 'ineligible' | 'error';

/**
 * Whether the owner still has the free period.
 *
 * The Housien decides and tells Payien (RN-05), so the screen reads the same
 * answer instead of inferring it from the catalogue. Until it arrives, and if
 * it never does, the status is not `eligible`: a badge must never promise
 * what the charge will not keep (AR-07).
 *
 * @param agencyId - The agency whose owner to ask about; omitted during
 *   onboarding, before the agency exists, to ask about the signed-in user.
 * @returns The status.
 */
export function useTrialEligibility(agencyId?: string): TrialStatus {
  const [state, setState] = useState<{ key?: string; status: TrialStatus }>({
    key: agencyId, status: 'loading',
  });

  useEffect(() => {
    let cancelled = false;
    setState({ key: agencyId, status: 'loading' });
    SubscriptionService.getTrialEligibility(agencyId)
      .then((answer) => settle(answer.trial_eligible ? 'eligible' : 'ineligible'))
      .catch((err) => {
        console.error('Could not read trial eligibility', err);
        settle('error');
      });
    function settle(status: TrialStatus) {
      if (!cancelled) setState({ key: agencyId, status });
    }
    return () => { cancelled = true; };
  }, [agencyId]);

  // A stale answer for the previous agency is never shown for the new one.
  return state.key === agencyId ? state.status : 'loading';
}
